import { canPerform } from '~publish/utils/permissions/canPerform'

/**
 * View-only access scope for a set of rendered channels.
 *
 * - `none` — no channel is view-only.
 * - `some` — a subset (but not all) of the channels are view-only.
 * - `all`  — every channel is view-only.
 */
export type ViewOnlyScenario = 'none' | 'some' | 'all'

/** The minimal channel shape needed to classify view-only access. */
export type ViewOnlyChannel = {
  allowedActions?: readonly string[] | null
}

/**
 * A channel is **view-only** when the actor can see comments but cannot manage
 * them (reply / like / resolve).
 */
export const isViewOnlyChannel = (channel: ViewOnlyChannel): boolean =>
  canPerform(channel, 'viewComments') && !canPerform(channel, 'manageComments')

export const VIEW_ONLY_COMMUNITY_CHANNEL_TOOLTIP =
  'You have view-only access in Community to this channel.'

/**
 * Classify a set of channels by how much of it is view-only. Used to decide
 * the view-only messaging: the header badge shows only for `all`
 * ({@link ViewOnlyHintButton}), while comment cards carry the per-card
 * indicator (the `some` scenario in the All Channels view).
 *
 * An empty set is `none`.
 */
export const getViewOnlyScenario = (
  channels: ViewOnlyChannel[],
): ViewOnlyScenario => {
  const total = channels.length
  if (total === 0) {
    return 'none'
  }

  const viewOnlyCount = channels.filter(isViewOnlyChannel).length

  if (viewOnlyCount === 0) {
    return 'none'
  }

  return viewOnlyCount === total ? 'all' : 'some'
}
